import { memo, useCallback } from "react"
import { useSelector } from "react-redux"
import { selectQuickWidgetConfig, selectQuickWidgets } from "../../selectors/quickWidgets"
import { Box, ListItemIcon, ListItemText, MenuItem, Typography, useTheme } from '@mui/material'
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import { useStyles } from './quickWidgetMenuItemStyles';
import { QuickWidgetTypes, updateUserPreferencesInState } from "../../reducer/user.reducer";
import { useDispatch } from "react-redux";
import { selectUserPreferences } from "../../selectors/user";
import { getQuickWidgetConfigs } from "../../util/quickWidgets";

export type QuickWidgetMenuItemProps = {
    text: string,
    widgetType: QuickWidgetTypes,
    leftPadding?: boolean,
    closeMenu: () => void,
    disabled: boolean,
    shortcutHint: string,
}

const QuickWidgetMenuItem: React.FC<QuickWidgetMenuItemProps> = ({ text, widgetType, leftPadding, closeMenu, disabled, shortcutHint }) => {

    const { classes, cx } = useStyles();
    const dispatch = useDispatch();
    const theme = useTheme()

    const quickWidgets = useSelector(selectQuickWidgets)
    const userPreferences = useSelector(selectUserPreferences)
    const config = useSelector(selectQuickWidgetConfig(widgetType))

    const toggleWidget = useCallback(() => {
        if (disabled) return;
        const existingWidgetConfig = quickWidgets?.[widgetType] || {};
        const newConfig = getQuickWidgetConfigs(widgetType, quickWidgets || {}, true)[widgetType];

        dispatch(updateUserPreferencesInState({
            ...userPreferences,
            quickWidgets: { ...(userPreferences.quickWidgets || {}),
                [widgetType]: { ...existingWidgetConfig, ...newConfig, },
            }
        }));
    }, [disabled, dispatch, quickWidgets, userPreferences, widgetType]);

    const onClick = useCallback(() => {
        toggleWidget();
        if (!disabled) closeMenu();
    }, [toggleWidget, disabled, closeMenu])

    return<>
        <MenuItem className={classes.item} onClick={onClick} disabled={disabled}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    minWidth: 200,
                    maxWidth: 250,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {leftPadding && (
                        <ListItemIcon
                            sx={{ minWidth: 'auto', marginRight: 0.1 }}
                            className={cx({
                                [classes.inactive]: !config?.enabled,
                            })}
                        >
                            <DoneOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                    )}
                    <ListItemText
                        primary={
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                {text}
                            </Typography>
                        }
                    />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                        component="kbd"
                        sx={{
                            padding: '2px 6px',
                            fontSize: '12px',
                            backgroundColor: theme.palette.background.default,
                            borderRadius: '4px',
                        }}
                    >
                        ⇧
                    </Box>
                    +
                    <Box
                        component="kbd"
                        sx={{
                            padding: '2px 6px',
                            fontSize: '12px',
                            backgroundColor: theme.palette.background.default,
                            borderRadius: '4px',
                        }}
                    >
                        {shortcutHint}
                    </Box>
                </Box>
            </Box>
        </MenuItem>
    </>
}


export default memo(QuickWidgetMenuItem);